package one.healthium.log_symptoms.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Icon
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.dp
import androidx.compose.ui.util.fastForEachIndexed
import androidx.lifecycle.Lifecycle
import androidx.lifecycle.compose.LifecycleEventEffect
import com.arkivanov.essenty.backhandler.BackCallback
import com.arkivanov.essenty.backhandler.BackHandler
import ifaandroid.feature.profile.log_symptoms.generated.resources.Res
import ifaandroid.feature.profile.log_symptoms.generated.resources.ic_severity_bad
import ifaandroid.feature.profile.log_symptoms.generated.resources.ic_severity_good
import ifaandroid.feature.profile.log_symptoms.generated.resources.ic_severity_neutral
import loco.LOG_SYMPTOMS_SCREEN_TITLE
import one.healthium.domain.user.models.SymptomSeverity
import one.healthium.loco.resolve
import one.healthium.ui.compose.components.AppBackNavigationButton
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.components.AppTopBar
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.white
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

interface LogSymptomsEventDelegate {
    fun onBackClicked()
}

@Composable
fun LogSymptomsScreen(
    taskId: String?,
    backHandler: BackHandler,
    eventDelegate: LogSymptomsEventDelegate,
) {
    val viewModel = koinViewModel<LogSymptomsViewModel>()
    val state by viewModel.state.collectAsState()

    LifecycleEventEffect(Lifecycle.Event.ON_PAUSE) {
        if (state.hasChanges) {
            viewModel.onSaveClicked(taskId, state)
        }
    }

    BackHandler(backHandler) {
        if (state.hasChanges) {
            viewModel.onSaveClicked(taskId, state)
        }
        eventDelegate.onBackClicked()
    }

    LogSymptomsScreenContent(
        state = state,
        onBackClicked = {
            if (state.hasChanges) {
                viewModel.onSaveClicked(taskId, state)
            }
            eventDelegate.onBackClicked()
        },
        onSeverityClicked = viewModel::onSymptomSeverityClicked,
    )
}

@Composable
private fun LogSymptomsScreenContent(
    state: LogSymptomsUiState,
    onBackClicked: () -> Unit,
    onSeverityClicked: (SymptomUiModel, SymptomSeverity) -> Unit,
) {
    AppScaffold(
        modifier = Modifier.testTag("log_symptoms_screen"),
        topBar = {
            AppTopBar(
                title = {
                    Text(stringResource(loco.Res.string.LOG_SYMPTOMS_SCREEN_TITLE))
                },
                navigationIcon = {
                    AppBackNavigationButton(
                        modifier = Modifier.testTag("log_symptoms_back_button"),
                        onClick = onBackClicked,
                    )
                }
            )
        }
    ) {
        Box(Modifier.padding(it)) {
            SymptomsList(
                uiData = state.uiData ?: emptyList(),
                onSeverityClicked = onSeverityClicked,
            )
        }
    }
}

@Composable
private fun SymptomsList(
    uiData: List<Any>,
    onSeverityClicked: (SymptomUiModel, SymptomSeverity) -> Unit,
) {
    LazyColumn(
        contentPadding = PaddingValues(horizontal = 12.dp, vertical = 24.dp),
    ) {
        items(uiData.size) { index ->
            val item = uiData[index]
            when (item) {
                is SymptomUiModel -> SymptomItem(symptom = item, onSeverityClicked = onSeverityClicked)
            }
            Spacer(Modifier.height(12.dp))
        }
    }
}

@Composable
private fun SymptomItem(
    modifier: Modifier = Modifier,
    symptom: SymptomUiModel,
    onSeverityClicked: (SymptomUiModel, SymptomSeverity) -> Unit,
) {
    Surface(
        modifier = modifier.fillMaxWidth(),
        color = AppTheme.colors.primary,
        shape = AppTheme.shapes.large,
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 12.dp, vertical = 7.dp)
                .testTag(symptom.kind.name),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Spacer(Modifier.width(12.dp))
            Text(
                modifier = Modifier.weight(1f),
                text = symptom.displayName.resolve(),
                style = AppTheme.textStyles.secondary_17_500,
                color = AppTheme.colors.onPrimary,
            )
            Spacer(Modifier.width(8.dp))
            symptom.severities.fastForEachIndexed { index, severity ->
                if (index > 0) {
                    Spacer(Modifier.width(12.dp))
                }
                SeverityButton(
                    severity = severity,
                    isSelected = severity == symptom.selectedSeverity,
                    onClick = { onSeverityClicked(symptom, severity) },
                )
            }
        }
    }
}

@Composable
private fun SeverityButton(
    severity: SymptomSeverity,
    isSelected: Boolean,
    onClick: () -> Unit,
) {
    Box(
        modifier = Modifier
            .size(40.dp)
            .background(
                color = if (isSelected) white else AppTheme.colors.secondary,
                shape = CircleShape,
            )
            .clip(CircleShape)
            .clickable(onClick = onClick)
            .testTag(severity.name),
        contentAlignment = Alignment.Center,
    ) {
        Icon(
            painter = painterResource(severity.icon),
            tint = if (isSelected) AppTheme.colors.secondary else AppTheme.colors.onSecondary,
            contentDescription = null,
        )
    }
}

private val SymptomSeverity.icon: DrawableResource
    get() = when (this) {
        SymptomSeverity.Good -> Res.drawable.ic_severity_good
        SymptomSeverity.Neutral -> Res.drawable.ic_severity_neutral
        SymptomSeverity.Bad -> Res.drawable.ic_severity_bad
    }

@Composable
fun BackHandler(backHandler: BackHandler, isEnabled: Boolean = true, onBack: () -> Unit) {
    val currentOnBack by rememberUpdatedState(onBack)

    val callback = remember {
        BackCallback(isEnabled = isEnabled) {
            currentOnBack()
        }
    }

    SideEffect { callback.isEnabled = isEnabled }

    DisposableEffect(backHandler) {
        backHandler.register(callback)
        onDispose { backHandler.unregister(callback) }
    }
}